import { isApiResponseSuccessful } from "../api/response.js";

// 红魔(努比亚)联合登录：登录态读写 + 授权登录 URL 构造与跳转。
// 加解密/验签全部在后端 /api/NubiaRecycle/UserLogin，前端只负责判断登录态、拼授权入口、落地存储。

// 登录回调接口只返回 userId / userName（不含 token），订单接口继续以已验证的 userId 作为 uid。
const STORAGE_UID = "uid";
const STORAGE_USER_NAME = "userName";

// 固定的前端登录中间页，仅负责接收红魔回传参数，与文档中的 redirect_uri 示例无关。
export const NUBIA_CALLBACK_PATH = "/oauth/nubia-callback";

const readStorage = (key) => {
  try {
    return globalThis.localStorage?.getItem(key) || "";
  } catch {
    return "";
  }
};

export const getNubiaUserId = () => readStorage(STORAGE_UID);
export const getNubiaUserName = () => readStorage(STORAGE_USER_NAME);

// userId 和 userName 都存在才视为已登录，避免部分缓存绕过登录流程。
export const hasNubiaLogin = () => Boolean(getNubiaUserId() && getNubiaUserName());

export const setNubiaLogin = ({ userId, userName } = {}) => {
  const normalizedUserId = String(userId || "");
  const normalizedUserName = String(userName || "");
  if (!normalizedUserId || !normalizedUserName) {
    clearNubiaLogin();
    return false;
  }

  try {
    globalThis.localStorage?.setItem(STORAGE_UID, normalizedUserId);
    globalThis.localStorage?.setItem(STORAGE_USER_NAME, normalizedUserName);
    return true;
  } catch {
    clearNubiaLogin();
    return false;
  }
};

export const clearNubiaLogin = () => {
  try {
    globalThis.localStorage?.removeItem(STORAGE_UID);
    globalThis.localStorage?.removeItem(STORAGE_USER_NAME);
  } catch {
    // 浏览器禁用存储时保持未登录状态，由页面重新发起授权。
  }
};

const normalizeQueryValue = (value) => {
  if (Array.isArray(value)) return String(value[0] || "");
  return value === undefined || value === null ? "" : String(value);
};

export const buildNubiaLoginPayload = (query = {}) => ({
  code: normalizeQueryValue(query.code),
  partnerId: normalizeQueryValue(query.partner_id),
  timestamp: normalizeQueryValue(query.timestamp),
  userName: normalizeQueryValue(query.username),
  sign: normalizeQueryValue(query.sign),
  ticket: normalizeQueryValue(query.ticket),
});

export const isNubiaLoginSuccessful = (response = {}) =>
  isApiResponseSuccessful(response) &&
  Boolean(response.data?.userId) &&
  Boolean(response.data?.userName);

// back 内容由回收侧自定义，红魔原样透传。用 encodeURIComponent + btoa 兼容中文，中转页对称解码。
export const encodeBack = (url) => btoa(encodeURIComponent(url));

// 把 back 还原成站内路径（pathname + search），供中转页登录后 router 跳转。
export const decodeBack = (raw, origin = globalThis.location?.origin || "http://localhost") => {
  if (!raw) return "/";
  try {
    const decoded = decodeURIComponent(atob(raw));
    const url = new URL(decoded, origin);
    return url.pathname + url.search;
  } catch {
    return "/";
  }
};

// 按文档三层结构拼装红魔授权登录地址；每层的 redirect_uri / back_url 都要整体编码，
// 否则内层的 &partner_id=... 会被外层 query 截断。
export const buildNubiaAuthUrl = (
  targetFullPath,
  env = import.meta.env || {},
  origin = globalThis.location?.origin || "http://localhost",
) => {
  const authOrigin = String(env.VITE_NUBIA_AUTH_ORIGIN || "");
  const partnerId = String(env.VITE_NUBIA_PARTNER_ID || "");
  if (!authOrigin || !partnerId) return "";

  try {
    // 固定中间页部署在当前 H5 域名下，生成完整 redirect_uri 后附加最终落地页 back。
    const callbackUrl = new URL(NUBIA_CALLBACK_PATH, origin);
    callbackUrl.searchParams.set("back", encodeBack(origin + targetFullPath));

    const accessUrl = new URL("/auth.php", authOrigin);
    accessUrl.searchParams.set("a", "access");
    accessUrl.searchParams.set("partner_id", partnerId);
    accessUrl.searchParams.set("redirect_uri", callbackUrl.href);

    const loginUrl = new URL("/auth.php", authOrigin);
    loginUrl.searchParams.set("a", "login");
    loginUrl.searchParams.set("back_url", accessUrl.href);
    return loginUrl.href;
  } catch {
    return "";
  }
};

// 无登录态时整页跳转红魔授权；用 replace 避免用户回退又落回未登录页触发二次跳转。
export const redirectToNubiaAuth = (to) => {
  const fullPath = typeof to === "string" ? to : to?.fullPath || "/";
  const url = buildNubiaAuthUrl(fullPath);
  if (url) globalThis.location.replace(url);
  return url;
};
